<template lang='pug'>
  div
    v-card(flat).mt-2
      v-card-title
        h3.text-h5.font-weight-light Run Configurations ({{count}})
        v-tooltip(right)
          template(v-slot:activator='{on}')
            v-btn(v-on='on' @click='create' icon color='primary')
              v-icon add
          span Add Run Configuration
      v-container(v-if='noRunConfigurations')
        v-row
          v-col(align='center' justify='center')
            .text-h5.grey--text No Run Configurations Defined
            v-btn(text color='accent' @click='create') Create Run Configuration
      run-configurations-table(v-if='!noRunConfigurations' v-model='dashboard.runConfigurations' @edit='edit' @remove='remove' @disable='disable')
    v-dialog(v-model='open', max-width='700px' persistent)
      v-card(v-if='active')
        v-form(ref='form' v-model='formValid')
          v-card-title
            h3.text-h5.font-weight-light {{createOrEdit}} Run Configuration
          v-card-text
            v-select(v-model='active.type' :items='types' label='Type' required :rules='required')
            select-suites(v-model='active.testSuites' :suites='availableSuites' :key='allSelected' required :rules='required') // the :key here will re-render the available suites on change of allSelected which has great effects
            v-select(v-if='active.type === "interval"' v-model='active.target.interval' :items='intervals' label='Run Interval' required :rules='required')
            v-text-field(v-if='active.type === "detect"' label='Detect URL' placeholder='https://example.com/some/path' v-model='active.target.url' type='text' required :rules='required')
            div(v-if='active.type === "detect"').text-caption
              | Auto-Run will watch the Detect URL and run trigger a run when changes are detected.
              | To enable auto-run, enter a URL that will change when new versions are deployed. This can be any URL that will respond to an HTTP GET.
            v-text-field(v-model='active.target.delaySeconds' label='Start Delay (seconds)' type='number' min='0' max='600' :rules='delayRules'
              hint='Number of seconds to wait before starting')
            v-checkbox(
              v-if='moreThanOneSuite'
              v-model='active.sequential'
              label='Run Sequentially'
              persistent-hint
              hint='If true, suites will run in order one after another')
          v-card-actions
            v-spacer
            v-btn(text @click='close') Cancel
            v-btn(color='primary' @click='save' text :loading='loading') {{createOrSave}}
</template>

<script>
import {cloneDeep, get} from 'lodash';
import Util from '@/api/util';
import RunConfigurationsTable from '@/components/RunConfigurationsTable';
import SelectSuites from '@/components/SelectSuites';
import Notifications from '@/services/notification.service';
import {mapGetters} from 'vuex';

export default {
  name: 'BionicMetricsRunConfigurations',
  components: {RunConfigurationsTable, SelectSuites},
  props: {
    dashboard: {
      type: Object
    },
    value: {
      type: Array,
      default() {
        return [];
      }
    }
  },
  computed: {
    count() {
      return this.value.length;
    },
    noRunConfigurations() {
      return !this.value.length;
    },
    createOrEdit() {
      return this.creating ? 'Create' : 'Edit';
    },
    createOrSave() {
      return this.creating ? 'Create' : 'Save';
    },
    moreThanOneSuite() {
      return this.active && this.active.testSuites && (this.allSelected || this.active.testSuites.length > 1);
    },
    allSelected() {
      if (!this.active || !this.active.testSuites) return false;
      for (const ts of this.active.testSuites) {
        if (ts.guid === 'all') return true;
      }
      return false;
    },
    availableSuites() {
      if (!this.dashboard || !this.dashboard.testSuites) return [];
      const avail = this.dashboard.testSuites.map((ts) => ({
        name: ts.name,
        description: ts.description,
        guid: ts.guid,
        testSuite: ts._id,
        disabled: this.allSelected
      }));
      return [{name: 'All', description: 'Run all suites', guid: 'all'}].concat(avail);
    },
    has20Minutes() {
      return get(this.account, 'entitlements.has20Minutes', false);
    },
    intervals() {
      if (this.has20Minutes) {
        return [{text: 'Every 20 Minutes', value: '20minutes'}, ...this.baseIntervals];
      }
      return this.baseIntervals;
    },
    ...mapGetters({
      account: 'getAccount'
    })
  },
  methods: {
    create() {
      this.creating = true;
      this.activeIndex = null;
      this.active = {
        enabled: true,
        type: 'interval',
        testSuites: [],
        target: {}
      };
      this.open = true;
    },
    edit(index) {
      this.creating = false;
      this.active = cloneDeep(this.value[index]); // {...this.value[index]}; <- this wasn't cloning nested references so target.url was the same as url when changing for detect
      this.activeIndex = index;
      this.open = true;
    },
    disable(index) {
      const edited = this.value.map((it, i) => {
        if (i === index) return {...it, enabled: !it.enabled};
        return it;
      });
      this.$emit('input', edited);
    },
    remove(index) {
      const removed = this.value.filter((it, i) => i !== index);
      this.$emit('input', removed);
    },
    close() {
      this.open = false;
    },
    needsUrlCheck() {
      if (this.active.type !== 'detect') return false;
      if (this.creating) return true;
      const {url} = this.active.target;
      const current = this.value[this.activeIndex];
      return url !== current.target.url;
    },
    async save() {
      if (!this.formValid) {
        this.$refs.form.validate();
        return;
      }
      if (!this.active.testSuites || !this.active.testSuites.length) {
        this.active.testSuites = [{guid: 'all', name: 'All'}];
      }
      if (this.needsUrlCheck()) {
        const lastHash = await this.checkDetectUrl();
        if (!lastHash) {
          return Notifications.alert(`<strong>${this.active.target.url}</strong> is either invalid or changing with each request. Please enter a different URL.`);
        }
        this.active.target.lastHash = lastHash;
      }

      if (this.creating) this.$emit('input', [...this.value, this.active]);
      else {
        const edited = this.value.map((it, i) => {
          if (i === this.activeIndex) return this.active;
          return it;
        });
        this.$emit('input', edited);
      }
      this.open = false;
      this.active = null;
      this.activeIndex = null;
    },
    async checkDetectUrl() {
      try {
        this.loading = true;
        const lastHash = await Util.checkUrl(this.active.target.url);
        this.loading = false;
        return lastHash;
      } catch (e) {
        this.loading = false;
        return null;
      }
    }
  },
  data() {
    return {
      open: false,
      creating: false,
      formValid: null,
      active: null,
      activeIndex: null,
      loading: false,
      types: [{
        text: 'Interval',
        value: 'interval'
      }, {
        text: 'Auto Detect',
        value: 'detect'
      }],
      baseIntervals: [
        {text: 'Every Hour', value: '1hour'},
        {text: 'Every 2 Hours', value: '2hours'},
        {text: 'Every 4 Hours', value: '4hours'},
        {text: 'Every 8 Hours', value: '8hours'},
        {text: 'Every 12 Hours', value: '12hours'},
        {text: 'Every Day', value: '1day'},
        {text: 'Every Other Day', value: '2days'},
        {text: 'Every Week', value: '1week'}
      ],
      required: [(v) => !!v || 'Required field'],
      delayRules: [
        (v) => !v || v >= 0 || 'Must be greater than or equal to 0',
        (v) => !v || v <= 1800 || 'Must be less than or equal to 30 minutes'
      ]
    };
  }
};
</script>

<style scoped>

</style>
